import { useState, useEffect, useMemo } from 'react';
import { Download, Trash2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { API, authFetch, formatDate, exportCSV } from './helpers';

const RegistrationsTab = ({ user }) => {
  const [registrations, setRegistrations] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRegistrations = async () => {
    const res = await authFetch(`${API}/registrations`);
    if (res.ok) setRegistrations(await res.json());
  };

  useEffect(() => { fetchRegistrations(); }, []);

  const filtered = useMemo(() => {
    let items = registrations;
    if (statusFilter !== 'all') items = items.filter(r => r.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter(r =>
        r.first_name?.toLowerCase().includes(q) || r.last_name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q)
      );
    }
    return items;
  }, [registrations, search, statusFilter]);

  const updateStatus = async (id, status) => {
    await authFetch(`${API}/registrations/${id}/status?status=${status}`, { method: 'PATCH' });
    fetchRegistrations();
  };

  const deleteRegistration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    await authFetch(`${API}/registrations/${id}`, { method: 'DELETE' });
    fetchRegistrations();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-brand-dark">Event Registrations</h2>
        <button onClick={() => exportCSV('registrations')} className="flex items-center gap-2 bg-white border border-gray-300 px-4 py-2 text-sm font-medium text-brand-dark hover:bg-gray-50" data-testid="export-registrations-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="registrations-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." className="pl-10" data-testid="registrations-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-gray-300 px-3 py-2 rounded text-sm" data-testid="registrations-status-filter">
          <option value="all">All Status</option>
          <option value="interested">Interested</option>
          <option value="registered">Registered</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <p className="text-xs text-gray-400 mb-4">{filtered.length} of {registrations.length} registrations</p>

      {filtered.length === 0 ? (
        <p className="text-gray-500">{registrations.length === 0 ? 'No event registrations yet.' : 'No registrations match your search.'}</p>
      ) : (
        <div className="overflow-x-auto" data-testid="registrations-table">
          <table className="w-full bg-white">
            <thead>
              <tr className="border-b">
                <th className="text-left p-4 text-sm font-medium text-gray-600">Name</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Email</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Phone</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Attendees</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Status</th>
                <th className="text-left p-4 text-sm font-medium text-gray-600">Date</th>
                {user?.role === 'admin' && <th className="text-left p-4 text-sm font-medium text-gray-600">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((reg) => (
                <tr key={reg.id} className="border-b hover:bg-gray-50" data-testid={`reg-${reg.id}`}>
                  <td className="p-4 text-sm">{reg.first_name} {reg.last_name}</td>
                  <td className="p-4 text-sm">{reg.email}</td>
                  <td className="p-4 text-sm">{reg.phone || '-'}</td>
                  <td className="p-4 text-sm">{reg.attendees}</td>
                  <td className="p-4">
                    <select value={reg.status} onChange={(e) => updateStatus(reg.id, e.target.value)} className="text-xs px-2 py-1 border rounded">
                      <option value="interested">Interested</option><option value="registered">Registered</option><option value="confirmed">Confirmed</option><option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="p-4 text-sm text-gray-500">{formatDate(reg.created_at)}</td>
                  {user?.role === 'admin' && (
                    <td className="p-4"><button onClick={() => deleteRegistration(reg.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RegistrationsTab;
